import { Dialog, Switch } from '@headlessui/react';
import {
  ArrowPathIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  PencilSquareIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useState } from 'react';
import BillingStatusBadge from '../components/billing/BillingStatusBadge';
import Pagination from '../components/common/Pagination';
import SortableTh from '../components/common/SortableTh';
import { useApiClient } from '../lib/api';
import { useNotification } from '../providers/notification-provider';
import { extractApiErrorMessage } from '../utils/apiErrorUtils';
import {
  ADMIN_BILLING_TYPE_OPTIONS,
  ASAAS_CREATION_STATUS_OPTIONS,
  ASAAS_STATUS_UPDATE_OPTIONS,
  BILLING_CYCLE_OPTIONS,
  EVENT_PROCESSING_OPTIONS,
  PROVIDER_OPTIONS,
  SIGNUP_STATUS_OPTIONS,
  SUBSCRIPTION_ORIGIN_OPTIONS,
  SUBSCRIPTION_STATUS_OPTIONS,
  adminBillingTypeLabel,
  cycleLabel,
  formatDate,
  formatDateTime,
  formatMoney,
  originLabel,
  providerLabel,
  toDateLocal,
  toDateTimeLocal
} from '../utils/billingUtils';

const PAGE_SIZE = 10;
const emptyPage = { items: [], totalItems: 0, totalPages: 1 };

const initialPlanForm = {
  id: null,
  code: '',
  productCode: 'waitercall',
  name: '',
  description: '',
  billingCycle: 'MONTHLY',
  priceAmount: '0,00',
  currency: 'BRL',
  isActive: true,
  isFeatured: false,
  isPubliclyVisible: true,
  displayOrder: '0',
  landingLabel: '',
  landingCaption: '',
  landingNote: '',
  providerReference: '',
  tableLimit: '20',
  restaurantLimit: '1'
};

const initialSubscriptionForm = {
  companyId: '',
  planId: '',
  provider: 'manual',
  origin: 'manual',
  status: 'active',
  priceAmount: '',
  currency: 'BRL',
  providerBillingType: 'PIX',
  providerDescription: '',
  startedAt: '',
  activatedAt: '',
  currentPeriodStart: '',
  currentPeriodEnd: '',
  nextDueAt: '',
  providerStatus: '',
  notes: '',
  manualBillingReference: ''
};

const initialStatusForm = {
  status: 'active',
  reason: '',
  providerStatus: '',
  nextDueAt: '',
  currentPeriodEnd: ''
};

const initialPriceForm = {
  priceAmount: '',
  currency: 'BRL',
  reason: ''
};

const MANUAL_SUBSCRIPTION_ORIGIN_OPTIONS = SUBSCRIPTION_ORIGIN_OPTIONS.filter(
  (option) => option.value !== 'self_service'
);

const normalizeDecimal = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim().replace(/\./g, '').replace(',', '.');
  if (!normalized.length) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const EmptyState = ({ title, description }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
    <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
    <p className="mt-2 text-sm text-slate-600">{description}</p>
  </div>
);

const OverviewCard = ({ title, value, icon: Icon, tone = 'slate' }) => {
  const toneClasses = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100',
    rose: 'bg-rose-50 text-rose-700 ring-rose-100',
    indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
    sky: 'bg-sky-50 text-sky-700 ring-sky-100',
    slate: 'bg-slate-50 text-slate-700 ring-slate-100'
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">{value}</p>
        </div>
        <span className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ring-1 ${toneClasses[tone] || toneClasses.slate}`}>
          <Icon className="h-6 w-6" />
        </span>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${active ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'}`}
  >
    {children}
  </button>
);

const InputField = ({ label, required, error, className = '', ...props }) => (
  <label className={`block ${className}`}>
    <span className="block text-sm font-medium text-slate-700">
      {label}
      {required ? <span className="text-rose-500"> *</span> : null}
    </span>
    <input
      {...props}
      className={`mt-1 block w-full rounded-xl border-0 px-3 py-2 text-sm text-slate-900 shadow-sm ring-1 ring-inset ${error ? 'ring-rose-300 focus:ring-rose-500' : 'ring-slate-300 focus:ring-indigo-600'} focus:ring-2`}
    />
    {error ? <span className="mt-1 block text-xs text-rose-600">{error}</span> : null}
  </label>
);

const SelectField = ({ label, required, options, error, className = '', ...props }) => (
  <label className={`block ${className}`}>
    <span className="block text-sm font-medium text-slate-700">
      {label}
      {required ? <span className="text-rose-500"> *</span> : null}
    </span>
    <select
      {...props}
      className={`mt-1 block w-full rounded-xl border-0 px-3 py-2 text-sm text-slate-900 shadow-sm ring-1 ring-inset ${error ? 'ring-rose-300 focus:ring-rose-500' : 'ring-slate-300 focus:ring-indigo-600'} focus:ring-2`}
    >
      <option value="">Selecione</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error ? <span className="mt-1 block text-xs text-rose-600">{error}</span> : null}
  </label>
);

const TextAreaField = ({ label, error, className = '', ...props }) => (
  <label className={`block ${className}`}>
    <span className="block text-sm font-medium text-slate-700">{label}</span>
    <textarea
      {...props}
      className={`mt-1 block min-h-24 w-full rounded-xl border-0 px-3 py-2 text-sm text-slate-900 shadow-sm ring-1 ring-inset ${error ? 'ring-rose-300 focus:ring-rose-500' : 'ring-slate-300 focus:ring-indigo-600'} focus:ring-2`}
    />
    {error ? <span className="mt-1 block text-xs text-rose-600">{error}</span> : null}
  </label>
);

const ModalShell = ({ open, onClose, title, children, maxWidth = 'max-w-5xl' }) => (
  <Dialog open={open} onClose={onClose} className="relative z-50">
    <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
    <div className="fixed inset-0 overflow-y-auto p-4">
      <Dialog.Panel className={`mx-auto max-h-[92dvh] w-full overflow-y-auto ${maxWidth} rounded-3xl bg-white p-4 shadow-2xl sm:p-6`}>
        <Dialog.Title className="border-b border-slate-200 pb-3 text-lg font-semibold text-slate-900">
          {title}
        </Dialog.Title>
        {children}
      </Dialog.Panel>
    </div>
  </Dialog>
);

const metaItemClass = 'rounded-2xl border border-slate-200 bg-slate-50 p-3';

export default function SubscriptionsPage() {
  const api = useApiClient();
  const { showToast, showConfirm } = useNotification();

  const [activeTab, setActiveTab] = useState('subscriptions');
  const [overview, setOverview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [subscriptions, setSubscriptions] = useState(emptyPage);
  const [signups, setSignups] = useState(emptyPage);
  const [events, setEvents] = useState(emptyPage);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [loadingSignups, setLoadingSignups] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [planSearch, setPlanSearch] = useState('');
  const [plansOnlyActive, setPlansOnlyActive] = useState(false);
  const [plansBillingCycleFilter, setPlansBillingCycleFilter] = useState('MONTHLY');

  const [subscriptionsQuery, setSubscriptionsQuery] = useState({
    page: 1,
    search: '',
    status: '',
    planId: '',
    origin: '',
    delinquentOnly: false,
    sortConfig: { key: 'createdAt', direction: 'desc' }
  });
  const [signupsQuery, setSignupsQuery] = useState({
    page: 1,
    search: '',
    status: '',
    sortConfig: { key: 'createdAt', direction: 'desc' }
  });
  const [eventsQuery, setEventsQuery] = useState({
    page: 1,
    search: '',
    eventType: '',
    processingStatus: '',
    sortConfig: { key: 'createdAt', direction: 'desc' }
  });

  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planForm, setPlanForm] = useState(initialPlanForm);
  const [planErrors, setPlanErrors] = useState({});
  const [planSaving, setPlanSaving] = useState(false);

  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState(initialSubscriptionForm);
  const [subscriptionErrors, setSubscriptionErrors] = useState({});
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [subscriptionDetail, setSubscriptionDetail] = useState(null);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusForm, setStatusForm] = useState(initialStatusForm);
  const [statusSaving, setStatusSaving] = useState(false);

  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [priceForm, setPriceForm] = useState(initialPriceForm);
  const [priceErrors, setPriceErrors] = useState({});
  const [priceSaving, setPriceSaving] = useState(false);

  const planOptions = useMemo(
    () => plans.map((plan) => ({ value: plan.id, label: `${plan.landingLabel || plan.name} · ${cycleLabel(plan.billingCycle)}` })),
    [plans]
  );

  const companyOptions = useMemo(
    () => companies.map((company) => ({ value: company.id, label: company.name })),
    [companies]
  );

  const subscriptionUsesAsaas = subscriptionForm.provider === 'asaas';
  const detailUsesAsaas = subscriptionDetail?.provider === 'asaas';
  const subscriptionStatusOptions = subscriptionUsesAsaas ? ASAAS_CREATION_STATUS_OPTIONS : SUBSCRIPTION_STATUS_OPTIONS;
  const statusUpdateOptions = detailUsesAsaas ? ASAAS_STATUS_UPDATE_OPTIONS : SUBSCRIPTION_STATUS_OPTIONS;

  const baseFilteredPlans = useMemo(() => {
    const normalizedSearch = planSearch.trim().toLowerCase();
    return plans.filter((plan) => {
      if (plansOnlyActive && !plan.isActive) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return [
        plan.code,
        plan.name,
        plan.landingLabel,
        plan.landingCaption,
        plan.billingCycle
      ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
    });
  }, [planSearch, plans, plansOnlyActive]);

  const planCountsByCycle = useMemo(
    () => BILLING_CYCLE_OPTIONS.reduce((accumulator, option) => ({
      ...accumulator,
      [option.value]: baseFilteredPlans.filter((plan) => plan.billingCycle === option.value).length
    }), {}),
    [baseFilteredPlans]
  );

  const filteredPlans = useMemo(
    () => baseFilteredPlans.filter((plan) => plan.billingCycle === plansBillingCycleFilter),
    [baseFilteredPlans, plansBillingCycleFilter]
  );

  const refreshOverview = useCallback(async () => {
    setLoadingOverview(true);
    try {
      const response = await api.get('/api/v1/admin/billing/overview');
      setOverview(response.data);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível carregar o panorama de assinaturas.'), 'error');
    } finally {
      setLoadingOverview(false);
    }
  }, [api, showToast]);

  const refreshPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const response = await api.get('/api/v1/admin/billing/plans', {
        params: { productCode: 'waitercall', onlyActive: false }
      });
      setPlans(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível carregar os planos.'), 'error');
    } finally {
      setLoadingPlans(false);
    }
  }, [api, showToast]);

  const refreshCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const response = await api.get('/api/v1/admin/companies', {
        params: { page: 1, pageSize: 100, includeInactive: true, sortBy: 'name', sortDesc: false }
      });
      setCompanies(response.data?.items || []);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível carregar as empresas.'), 'error');
    } finally {
      setLoadingCompanies(false);
    }
  }, [api, showToast]);

  const refreshSubscriptions = useCallback(async () => {
    setLoadingSubscriptions(true);
    try {
      const response = await api.get('/api/v1/admin/billing/subscriptions', {
        params: {
          page: subscriptionsQuery.page,
          pageSize: PAGE_SIZE,
          search: subscriptionsQuery.search || undefined,
          status: subscriptionsQuery.status || undefined,
          planId: subscriptionsQuery.planId || undefined,
          origin: subscriptionsQuery.origin || undefined,
          delinquentOnly: subscriptionsQuery.delinquentOnly || undefined,
          sortBy: subscriptionsQuery.sortConfig.key,
          sortDesc: subscriptionsQuery.sortConfig.direction === 'desc'
        }
      });
      setSubscriptions(response.data || emptyPage);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível carregar as assinaturas.'), 'error');
    } finally {
      setLoadingSubscriptions(false);
    }
  }, [api, showToast, subscriptionsQuery]);

  const refreshSignups = useCallback(async () => {
    setLoadingSignups(true);
    try {
      const response = await api.get('/api/v1/admin/billing/signups', {
        params: {
          page: signupsQuery.page,
          pageSize: PAGE_SIZE,
          search: signupsQuery.search || undefined,
          status: signupsQuery.status || undefined,
          sortBy: signupsQuery.sortConfig.key,
          sortDesc: signupsQuery.sortConfig.direction === 'desc'
        }
      });
      setSignups(response.data || emptyPage);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível carregar os cadastros públicos.'), 'error');
    } finally {
      setLoadingSignups(false);
    }
  }, [api, showToast, signupsQuery]);

  const refreshEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const response = await api.get('/api/v1/admin/billing/events', {
        params: {
          page: eventsQuery.page,
          pageSize: PAGE_SIZE,
          search: eventsQuery.search || undefined,
          eventType: eventsQuery.eventType || undefined,
          processingStatus: eventsQuery.processingStatus || undefined,
          sortBy: eventsQuery.sortConfig.key,
          sortDesc: eventsQuery.sortConfig.direction === 'desc'
        }
      });
      setEvents(response.data || emptyPage);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível carregar os eventos.'), 'error');
    } finally {
      setLoadingEvents(false);
    }
  }, [api, showToast, eventsQuery]);

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshOverview(), refreshPlans(), refreshCompanies()]);
    if (activeTab === 'subscriptions') {
      await refreshSubscriptions();
    } else if (activeTab === 'signups') {
      await refreshSignups();
    } else if (activeTab === 'events') {
      await refreshEvents();
    }
  }, [activeTab, refreshCompanies, refreshEvents, refreshOverview, refreshPlans, refreshSignups, refreshSubscriptions]);

  useEffect(() => {
    document.title = 'Assinaturas | Orion WaiterCall';
    refreshOverview();
    refreshPlans();
    refreshCompanies();
  }, [refreshCompanies, refreshOverview, refreshPlans]);

  useEffect(() => {
    if (activeTab === 'subscriptions') {
      refreshSubscriptions();
    }
  }, [activeTab, refreshSubscriptions]);

  useEffect(() => {
    if (activeTab === 'signups') {
      refreshSignups();
    }
  }, [activeTab, refreshSignups]);

  useEffect(() => {
    if (activeTab === 'events') {
      refreshEvents();
    }
  }, [activeTab, refreshEvents]);

  const openCreatePlan = () => {
    setPlanErrors({});
    setPlanForm(initialPlanForm);
    setPlanModalOpen(true);
  };

  const openEditPlan = (plan) => {
    setPlanErrors({});
    setPlanForm({
      id: plan.id,
      code: plan.code || '',
      productCode: plan.productCode || 'waitercall',
      name: plan.name || '',
      description: plan.description || '',
      billingCycle: plan.billingCycle || 'MONTHLY',
      priceAmount: String(plan.priceAmount ?? '').replace('.', ','),
      currency: plan.currency || 'BRL',
      isActive: Boolean(plan.isActive),
      isFeatured: Boolean(plan.isFeatured),
      isPubliclyVisible: Boolean(plan.isPubliclyVisible),
      displayOrder: String(plan.displayOrder ?? 0),
      landingLabel: plan.landingLabel || '',
      landingCaption: plan.landingCaption || '',
      landingNote: plan.landingNote || '',
      providerReference: plan.providerReference || '',
      tableLimit: String(plan.tableLimit ?? 1),
      restaurantLimit: String(plan.restaurantLimit ?? 1)
    });
    setPlanModalOpen(true);
  };

  const submitPlan = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    const priceAmount = normalizeDecimal(planForm.priceAmount);
    const tableLimit = Number(planForm.tableLimit);
    const restaurantLimit = Number(planForm.restaurantLimit);

    if (!planForm.code.trim() && !planForm.id) nextErrors.code = 'Informe o código do plano.';
    if (!planForm.name.trim()) nextErrors.name = 'Informe o nome do plano.';
    if (!Number.isFinite(priceAmount) || priceAmount < 0) nextErrors.priceAmount = 'Informe um valor válido.';
    if (!Number.isInteger(tableLimit) || tableLimit <= 0) nextErrors.tableLimit = 'Informe um limite de mesas válido.';
    if (!Number.isInteger(restaurantLimit) || restaurantLimit <= 0) nextErrors.restaurantLimit = 'Informe um limite de restaurantes válido.';

    setPlanErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      showToast('Revise os dados do plano antes de salvar.', 'warning');
      return;
    }

    if (planForm.id && planForm.isActive === false) {
      const confirmed = await showConfirm({
        title: 'Inativar plano',
        message: `Deseja inativar o plano ${planForm.name}?`,
        confirmText: 'Inativar',
        cancelText: 'Cancelar',
        type: 'warning'
      });
      if (!confirmed) {
        return;
      }
    }

    setPlanSaving(true);
    try {
      const payload = {
        productCode: planForm.productCode.trim() || 'waitercall',
        code: planForm.code.trim(),
        name: planForm.name.trim(),
        description: planForm.description.trim() || null,
        billingCycle: planForm.billingCycle,
        priceAmount,
        currency: planForm.currency.trim() || 'BRL',
        isActive: planForm.isActive,
        isFeatured: planForm.isFeatured,
        isPubliclyVisible: planForm.isPubliclyVisible,
        displayOrder: Number(planForm.displayOrder) || 0,
        landingLabel: planForm.landingLabel.trim() || null,
        landingCaption: planForm.landingCaption.trim() || null,
        landingNote: planForm.landingNote.trim() || null,
        providerReference: planForm.providerReference.trim() || null,
        tableLimit,
        restaurantLimit
      };

      if (planForm.id) {
        await api.put(`/api/v1/admin/billing/plans/${planForm.id}`, payload);
        showToast('Plano atualizado com sucesso.', 'success');
      } else {
        await api.post('/api/v1/admin/billing/plans', payload);
        showToast('Plano criado com sucesso.', 'success');
      }

      setPlanModalOpen(false);
      await Promise.all([refreshPlans(), refreshOverview()]);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível salvar o plano.'), 'error');
    } finally {
      setPlanSaving(false);
    }
  };

  const openCreateSubscription = () => {
    setSubscriptionErrors({});
    setSubscriptionForm({
      ...initialSubscriptionForm,
      nextDueAt: toDateLocal(new Date().toISOString())
    });
    setSubscriptionModalOpen(true);
  };

  const submitSubscription = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    const priceAmount = subscriptionForm.priceAmount ? normalizeDecimal(subscriptionForm.priceAmount) : null;
    const statusValue = subscriptionUsesAsaas ? 'pending_payment' : subscriptionForm.status;

    if (!subscriptionForm.companyId) nextErrors.companyId = 'Selecione a empresa.';
    if (!subscriptionForm.planId) nextErrors.planId = 'Selecione o plano.';
    if (!statusValue) nextErrors.status = 'Selecione o status da assinatura.';
    if (subscriptionForm.provider === 'asaas' && !subscriptionForm.providerBillingType) nextErrors.providerBillingType = 'Selecione a forma de cobrança.';
    if (subscriptionForm.provider === 'asaas' && !subscriptionForm.nextDueAt) nextErrors.nextDueAt = 'Informe a próxima cobrança.';
    if (subscriptionForm.priceAmount && (!Number.isFinite(priceAmount) || priceAmount < 0)) nextErrors.priceAmount = 'Informe um valor válido.';

    setSubscriptionErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      showToast('Revise os dados da assinatura antes de salvar.', 'warning');
      return;
    }

    setSubscriptionSaving(true);
    try {
      await api.post('/api/v1/admin/billing/subscriptions', {
        companyId: subscriptionForm.companyId,
        planId: subscriptionForm.planId,
        status: statusValue,
        origin: subscriptionForm.origin,
        priceAmount,
        currency: subscriptionForm.currency.trim() || null,
        startedAt: subscriptionForm.startedAt || null,
        activatedAt: subscriptionUsesAsaas ? null : subscriptionForm.activatedAt || null,
        currentPeriodStart: subscriptionUsesAsaas ? null : subscriptionForm.currentPeriodStart || null,
        currentPeriodEnd: subscriptionUsesAsaas ? null : subscriptionForm.currentPeriodEnd || null,
        nextDueAt: subscriptionForm.nextDueAt || null,
        provider: subscriptionForm.provider,
        providerBillingType: subscriptionForm.provider === 'asaas' ? subscriptionForm.providerBillingType : null,
        providerDescription: subscriptionForm.provider === 'asaas' ? subscriptionForm.providerDescription.trim() || null : null,
        providerStatus: subscriptionUsesAsaas ? null : subscriptionForm.providerStatus.trim() || null,
        notes: subscriptionForm.notes.trim() || null,
        manualBillingReference: subscriptionForm.manualBillingReference.trim() || null
      });

      setSubscriptionModalOpen(false);
      showToast('Assinatura criada com sucesso.', 'success');
      await Promise.all([refreshSubscriptions(), refreshOverview()]);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível criar a assinatura.'), 'error');
    } finally {
      setSubscriptionSaving(false);
    }
  };

  const openSubscriptionDetail = async (subscriptionId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const response = await api.get(`/api/v1/admin/billing/subscriptions/${subscriptionId}`);
      setSubscriptionDetail(response.data);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível carregar os detalhes da assinatura.'), 'error');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openStatusModal = (subscription) => {
    const usesAsaas = subscription.provider === 'asaas';
    const allowedStatuses = usesAsaas ? ASAAS_STATUS_UPDATE_OPTIONS.map((item) => item.value) : SUBSCRIPTION_STATUS_OPTIONS.map((item) => item.value);
    const nextStatus = allowedStatuses.includes(subscription.status) ? subscription.status : '';

    setStatusForm({
      status: nextStatus,
      reason: '',
      providerStatus: usesAsaas ? '' : subscription.providerStatus || '',
      nextDueAt: toDateTimeLocal(subscription.nextDueAt),
      currentPeriodEnd: usesAsaas ? '' : toDateTimeLocal(subscription.currentPeriodEnd)
    });
    setStatusModalOpen(true);
  };

  const submitStatusUpdate = async (event) => {
    event.preventDefault();
    if (!subscriptionDetail) {
      return;
    }

    if (!statusForm.status) {
      showToast('Selecione o novo status da assinatura.', 'warning');
      return;
    }

    if (statusForm.status === 'suspended' || statusForm.status === 'cancelled') {
      const confirmed = await showConfirm({
        title: statusForm.status === 'cancelled' ? 'Cancelar assinatura' : 'Suspender assinatura',
        message: `Deseja realmente alterar o status da assinatura da empresa ${subscriptionDetail.company?.name}?`,
        confirmText: 'Confirmar',
        cancelText: 'Cancelar',
        type: 'warning'
      });
      if (!confirmed) {
        return;
      }
    }

    setStatusSaving(true);
    try {
      const response = await api.post(`/api/v1/admin/billing/subscriptions/${subscriptionDetail.id}/status`, {
        status: statusForm.status,
        reason: statusForm.reason.trim() || null,
        providerStatus: detailUsesAsaas ? null : statusForm.providerStatus.trim() || null,
        nextDueAt: statusForm.nextDueAt || null,
        currentPeriodEnd: detailUsesAsaas ? null : statusForm.currentPeriodEnd || null
      });
      setSubscriptionDetail(response.data);
      setStatusModalOpen(false);
      showToast('Status atualizado com sucesso.', 'success');
      await Promise.all([refreshSubscriptions(), refreshOverview()]);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível atualizar o status da assinatura.'), 'error');
    } finally {
      setStatusSaving(false);
    }
  };

  const openPriceModal = (subscription) => {
    setPriceErrors({});
    setPriceForm({
      priceAmount: String(subscription.priceAmount ?? '').replace('.', ','),
      currency: subscription.currency || 'BRL',
      reason: ''
    });
    setPriceModalOpen(true);
  };

  const submitPriceUpdate = async (event) => {
    event.preventDefault();
    if (!subscriptionDetail) {
      return;
    }

    const priceAmount = normalizeDecimal(priceForm.priceAmount);
    const nextErrors = {};
    if (!Number.isFinite(priceAmount) || priceAmount < 0) {
      nextErrors.priceAmount = 'Informe um valor válido.';
    }
    setPriceErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showToast('Revise o valor informado antes de salvar.', 'warning');
      return;
    }

    setPriceSaving(true);
    try {
      const response = await api.post(`/api/v1/admin/billing/subscriptions/${subscriptionDetail.id}/price`, {
        priceAmount,
        currency: priceForm.currency.trim() || null,
        reason: priceForm.reason.trim() || null
      });
      setSubscriptionDetail(response.data);
      setPriceModalOpen(false);
      showToast('Preço atualizado com sucesso.', 'success');
      await Promise.all([refreshSubscriptions(), refreshOverview(), refreshPlans()]);
    } catch (error) {
      showToast(extractApiErrorMessage(error, 'Não foi possível atualizar o preço da assinatura.'), 'error');
    } finally {
      setPriceSaving(false);
    }
  };

  const handleSubscriptionsSort = (key) => {
    setSubscriptionsQuery((current) => ({
      ...current,
      page: 1,
      sortConfig: {
        key,
        direction: current.sortConfig.key === key && current.sortConfig.direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const handleSignupsSort = (key) => {
    setSignupsQuery((current) => ({
      ...current,
      page: 1,
      sortConfig: {
        key,
        direction: current.sortConfig.key === key && current.sortConfig.direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const handleEventsSort = (key) => {
    setEventsQuery((current) => ({
      ...current,
      page: 1,
      sortConfig: {
        key,
        direction: current.sortConfig.key === key && current.sortConfig.direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Assinaturas</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Gerencie catálogo, assinaturas, cadastros públicos e eventos de cobrança do Orion WaiterCall com o mesmo backend que abastece a landing do Site Corporativo.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={refreshAll} className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50">
            <ArrowPathIcon className="h-4 w-4" />
            Atualizar
          </button>
          <button type="button" onClick={openCreateSubscription} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
            <PlusIcon className="h-4 w-4" />
            Nova assinatura
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <OverviewCard title="Assinaturas ativas" value={loadingOverview ? '...' : overview?.subscriptionsActive ?? 0} icon={CreditCardIcon} tone="emerald" />
        <OverviewCard title="Em atraso" value={loadingOverview ? '...' : overview?.subscriptionsPastDue ?? 0} icon={BanknotesIcon} tone="amber" />
        <OverviewCard title="Self service" value={loadingOverview ? '...' : overview?.subscriptionsSelfService ?? 0} icon={BuildingOffice2Icon} tone="indigo" />
        <OverviewCard title="Cadastros abertos" value={loadingOverview ? '...' : overview?.signupsOpen ?? 0} icon={ClipboardDocumentListIcon} tone="sky" />
      </div>

      <div className="flex flex-wrap gap-2">
        <TabButton active={activeTab === 'subscriptions'} onClick={() => setActiveTab('subscriptions')}>Assinaturas</TabButton>
        <TabButton active={activeTab === 'plans'} onClick={() => setActiveTab('plans')}>Planos</TabButton>
        <TabButton active={activeTab === 'signups'} onClick={() => setActiveTab('signups')}>Cadastros públicos</TabButton>
        <TabButton active={activeTab === 'events'} onClick={() => setActiveTab('events')}>Eventos</TabButton>
      </div>

      {activeTab === 'subscriptions' ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid flex-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <InputField label="Buscar" placeholder="Empresa ou identificador" value={subscriptionsQuery.search} onChange={(event) => setSubscriptionsQuery((current) => ({ ...current, page: 1, search: event.target.value }))} />
              <SelectField label="Status" value={subscriptionsQuery.status} options={SUBSCRIPTION_STATUS_OPTIONS} onChange={(event) => setSubscriptionsQuery((current) => ({ ...current, page: 1, status: event.target.value }))} />
              <SelectField label="Plano" value={subscriptionsQuery.planId} options={planOptions} onChange={(event) => setSubscriptionsQuery((current) => ({ ...current, page: 1, planId: event.target.value }))} />
              <SelectField label="Origem" value={subscriptionsQuery.origin} options={SUBSCRIPTION_ORIGIN_OPTIONS} onChange={(event) => setSubscriptionsQuery((current) => ({ ...current, page: 1, origin: event.target.value }))} />
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">Apenas inadimplentes</p>
                  <p className="text-xs text-slate-500">Em atraso ou suspensas</p>
                </div>
                <Switch checked={subscriptionsQuery.delinquentOnly} onChange={(checked) => setSubscriptionsQuery((current) => ({ ...current, page: 1, delinquentOnly: checked }))} className={`${subscriptionsQuery.delinquentOnly ? 'bg-indigo-600' : 'bg-slate-300'} relative inline-flex h-6 w-11 items-center rounded-full`}>
                  <span className={`${subscriptionsQuery.delinquentOnly ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`} />
                </Switch>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-3 lg:hidden">
            {loadingSubscriptions ? (
              <EmptyState title="Carregando assinaturas" description="Aguarde enquanto buscamos os registros." />
            ) : subscriptions.items.length === 0 ? (
              <EmptyState title="Nenhuma assinatura encontrada" description="Ajuste os filtros ou cadastre uma nova assinatura." />
            ) : subscriptions.items.map((item) => (
              <button type="button" key={item.id} onClick={() => openSubscriptionDetail(item.id)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{item.company?.name}</h3>
                    <p className="mt-1 text-xs text-slate-500">{item.plan?.landingLabel || item.plan?.name}</p>
                  </div>
                  <BillingStatusBadge status={item.status} />
                </div>
                <dl className="mt-3 grid gap-2 text-sm text-slate-600">
                  <div><dt className="font-medium text-slate-500">Valor</dt><dd>{formatMoney(item.priceAmount, item.currency)}</dd></div>
                  <div><dt className="font-medium text-slate-500">Origem</dt><dd>{originLabel(item.origin)}</dd></div>
                  <div><dt className="font-medium text-slate-500">Próximo vencimento</dt><dd>{formatDate(item.nextDueAt)}</dd></div>
                </dl>
              </button>
            ))}
          </div>

          <div className="mt-6 hidden overflow-x-auto lg:block">
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr>
                  <SortableTh className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900" sortKey="createdAt" currentSort={subscriptionsQuery.sortConfig} onSort={handleSubscriptionsSort}>Empresa</SortableTh>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Plano</th>
                  <SortableTh className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900" sortKey="status" currentSort={subscriptionsQuery.sortConfig} onSort={handleSubscriptionsSort}>Status</SortableTh>
                  <SortableTh className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900" sortKey="priceAmount" currentSort={subscriptionsQuery.sortConfig} onSort={handleSubscriptionsSort}>Valor</SortableTh>
                  <SortableTh className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900" sortKey="nextDueAt" currentSort={subscriptionsQuery.sortConfig} onSort={handleSubscriptionsSort}>Próximo vencimento</SortableTh>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Origem</th>
                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-slate-900">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loadingSubscriptions ? (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-500">Carregando assinaturas...</td></tr>
                ) : subscriptions.items.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-500">Nenhuma assinatura encontrada com os filtros atuais.</td></tr>
                ) : subscriptions.items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-3 py-4 text-sm text-slate-900">
                      <button type="button" onClick={() => openSubscriptionDetail(item.id)} className="text-left">
                        <span className="font-semibold">{item.company?.name}</span>
                        <span className="mt-1 block text-xs text-slate-500">{item.company?.document}</span>
                      </button>
                    </td>
                    <td className="px-3 py-4 text-sm text-slate-600">{item.plan?.landingLabel || item.plan?.name}</td>
                    <td className="px-3 py-4 text-sm"><BillingStatusBadge status={item.status} /></td>
                    <td className="px-3 py-4 text-sm text-slate-600">{formatMoney(item.priceAmount, item.currency)}</td>
                    <td className="px-3 py-4 text-sm text-slate-600">{formatDate(item.nextDueAt)}</td>
                    <td className="px-3 py-4 text-sm text-slate-600">{originLabel(item.origin)}</td>
                    <td className="px-3 py-4 text-right text-sm"><button type="button" onClick={() => openSubscriptionDetail(item.id)} className="text-indigo-600 hover:text-indigo-800">Detalhes</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loadingSubscriptions && subscriptions.totalItems > 0 ? (
            <Pagination currentPage={subscriptionsQuery.page} totalPages={subscriptions.totalPages || 1} onPageChange={(page) => setSubscriptionsQuery((current) => ({ ...current, page }))} totalItems={subscriptions.totalItems} pageSize={PAGE_SIZE} />
          ) : null}
        </section>
      ) : null}

      {activeTab === 'plans' ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="grid flex-1 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr),minmax(280px,320px),220px]">
              <InputField label="Buscar plano" placeholder="Código, nome ou rótulo" value={planSearch} onChange={(event) => setPlanSearch(event.target.value)} />
              <div className="rounded-2xl border border-slate-200 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Periodicidade</p>
                    <p className="text-xs text-slate-500">Exibir catálogo por ciclo</p>
                  </div>
                </div>
                <div className="mt-3 inline-flex w-full rounded-2xl bg-slate-100 p-1">
                  {BILLING_CYCLE_OPTIONS.map((option) => {
                    const isActive = plansBillingCycleFilter === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setPlansBillingCycleFilter(option.value)}
                        className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition ${isActive ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                      >
                        <span>{option.label}</span>
                        <span className={`ml-2 inline-flex min-w-6 items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${isActive ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-200 text-slate-600'}`}>
                          {planCountsByCycle[option.value] ?? 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">Apenas ativos</p>
                  <p className="text-xs text-slate-500">Filtrar catálogo operacional</p>
                </div>
                <Switch checked={plansOnlyActive} onChange={setPlansOnlyActive} className={`${plansOnlyActive ? 'bg-indigo-600' : 'bg-slate-300'} relative inline-flex h-6 w-11 items-center rounded-full`}>
                  <span className={`${plansOnlyActive ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`} />
                </Switch>
              </div>
            </div>
            <button type="button" onClick={openCreatePlan} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
              <PlusIcon className="h-4 w-4" />
              Novo plano
            </button>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {loadingPlans ? (
              <EmptyState title="Carregando planos" description="Aguarde enquanto buscamos o catálogo." />
            ) : filteredPlans.length === 0 ? (
              <EmptyState title="Nenhum plano encontrado" description="Ajuste a busca, a periodicidade ou cadastre um novo plano." />
            ) : filteredPlans.map((plan) => (
              <article key={plan.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">{cycleLabel(plan.billingCycle)}</p>
                    <h3 className="mt-1 text-lg font-semibold text-slate-900">{plan.landingLabel || plan.name}</h3>
                    <p className="mt-1 text-sm text-slate-500">{plan.code}</p>
                  </div>
                  <BillingStatusBadge status={plan.isActive ? 'active' : 'cancelled'} />
                </div>
                <p className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">{formatMoney(plan.priceAmount, plan.currency)}</p>
                <p className="mt-2 text-sm text-slate-600">{plan.landingCaption || plan.description || 'Sem descrição informada.'}</p>
                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className={metaItemClass}><dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Mesas</dt><dd className="mt-1 font-semibold text-slate-900">{plan.tableLimit}</dd></div>
                  <div className={metaItemClass}><dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Restaurantes</dt><dd className="mt-1 font-semibold text-slate-900">{plan.restaurantLimit}</dd></div>
                </dl>
                <div className="mt-4 flex flex-wrap gap-2">
                  {plan.isPubliclyVisible ? <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">Visível no site</span> : null}
                  {plan.isFeatured ? <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">Destacado</span> : null}
                </div>
                <button type="button" onClick={() => openEditPlan(plan)} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800">
                  <PencilSquareIcon className="h-4 w-4" />
                  Editar plano
                </button>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {activeTab === 'signups' ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <InputField label="Buscar" placeholder="Empresa, documento ou e-mail" value={signupsQuery.search} onChange={(event) => setSignupsQuery((current) => ({ ...current, page: 1, search: event.target.value }))} />
            <SelectField label="Status" value={signupsQuery.status} options={SIGNUP_STATUS_OPTIONS} onChange={(event) => setSignupsQuery((current) => ({ ...current, page: 1, status: event.target.value }))} />
          </div>

          <div className="mt-6 space-y-3 lg:hidden">
            {loadingSignups ? (
              <EmptyState title="Carregando cadastros" description="Aguarde enquanto buscamos os registros." />
            ) : signups.items.length === 0 ? (
              <EmptyState title="Nenhum cadastro público encontrado" description="Os novos signups do self service aparecerão aqui." />
            ) : signups.items.map((item) => (
              <article key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{item.companyTradeName}</h3>
                    <p className="mt-1 text-xs text-slate-500">{item.adminEmail}</p>
                  </div>
                  <BillingStatusBadge status={item.status} />
                </div>
                <p className="mt-3 text-sm text-slate-600">Plano: {item.plan?.landingLabel || item.plan?.name || '-'}</p>
                <p className="mt-1 text-sm text-slate-600">Criado em: {formatDateTime(item.createdAt)}</p>
              </article>
            ))}
          </div>

          <div className="mt-6 hidden overflow-x-auto lg:block">
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr>
                  <SortableTh className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900" sortKey="createdAt" currentSort={signupsQuery.sortConfig} onSort={handleSignupsSort}>Empresa</SortableTh>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Plano</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Administrador</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Status</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Expira em</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loadingSignups ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">Carregando cadastros...</td></tr>
                ) : signups.items.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">Nenhum cadastro encontrado com os filtros atuais.</td></tr>
                ) : signups.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-3 py-4 text-sm text-slate-900">
                      <span className="font-semibold">{item.companyTradeName}</span>
                      <span className="mt-1 block text-xs text-slate-500">{item.companyDocument}</span>
                    </td>
                    <td className="px-3 py-4 text-sm text-slate-600">{item.plan?.landingLabel || item.plan?.name || '-'}</td>
                    <td className="px-3 py-4 text-sm text-slate-600">{item.adminEmail}</td>
                    <td className="px-3 py-4 text-sm"><BillingStatusBadge status={item.status} /></td>
                    <td className="px-3 py-4 text-sm text-slate-600">{formatDateTime(item.checkoutExpiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loadingSignups && signups.totalItems > 0 ? (
            <Pagination currentPage={signupsQuery.page} totalPages={signups.totalPages || 1} onPageChange={(page) => setSignupsQuery((current) => ({ ...current, page }))} totalItems={signups.totalItems} pageSize={PAGE_SIZE} />
          ) : null}
        </section>
      ) : null}

      {activeTab === 'events' ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <InputField label="Buscar" placeholder="Evento, empresa ou signup" value={eventsQuery.search} onChange={(event) => setEventsQuery((current) => ({ ...current, page: 1, search: event.target.value }))} />
            <InputField label="Tipo do evento" placeholder="Ex.: PAYMENT_RECEIVED" value={eventsQuery.eventType} onChange={(event) => setEventsQuery((current) => ({ ...current, page: 1, eventType: event.target.value }))} />
            <SelectField label="Processamento" value={eventsQuery.processingStatus} options={EVENT_PROCESSING_OPTIONS} onChange={(event) => setEventsQuery((current) => ({ ...current, page: 1, processingStatus: event.target.value }))} />
          </div>

          <div className="mt-6 space-y-3 lg:hidden">
            {loadingEvents ? (
              <EmptyState title="Carregando eventos" description="Aguarde enquanto buscamos o histórico." />
            ) : events.items.length === 0 ? (
              <EmptyState title="Nenhum evento encontrado" description="Os eventos de webhook e processamento aparecerão aqui." />
            ) : events.items.map((item) => (
              <article key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{item.eventType}</h3>
                    <p className="mt-1 text-xs text-slate-500">{item.providerEventId}</p>
                  </div>
                  <BillingStatusBadge status={item.processingStatus} />
                </div>
                <p className="mt-3 text-sm text-slate-600">Empresa: {item.companyName || '-'}</p>
                <p className="mt-1 text-sm text-slate-600">Criado em: {formatDateTime(item.createdAt)}</p>
              </article>
            ))}
          </div>

          <div className="mt-6 hidden overflow-x-auto lg:block">
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr>
                  <SortableTh className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900" sortKey="createdAt" currentSort={eventsQuery.sortConfig} onSort={handleEventsSort}>Evento</SortableTh>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Empresa</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Signup</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Status</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Criado em</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loadingEvents ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">Carregando eventos...</td></tr>
                ) : events.items.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">Nenhum evento encontrado com os filtros atuais.</td></tr>
                ) : events.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-3 py-4 text-sm text-slate-900">
                      <span className="font-semibold">{item.eventType}</span>
                      <span className="mt-1 block text-xs text-slate-500">{item.providerEventId}</span>
                    </td>
                    <td className="px-3 py-4 text-sm text-slate-600">{item.companyName || '-'}</td>
                    <td className="px-3 py-4 text-sm text-slate-600">{item.signupPublicId || '-'}</td>
                    <td className="px-3 py-4 text-sm"><BillingStatusBadge status={item.processingStatus} /></td>
                    <td className="px-3 py-4 text-sm text-slate-600">{formatDateTime(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loadingEvents && events.totalItems > 0 ? (
            <Pagination currentPage={eventsQuery.page} totalPages={events.totalPages || 1} onPageChange={(page) => setEventsQuery((current) => ({ ...current, page }))} totalItems={events.totalItems} pageSize={PAGE_SIZE} />
          ) : null}
        </section>
      ) : null}

      <ModalShell open={planModalOpen} onClose={() => setPlanModalOpen(false)} title={planForm.id ? 'Editar plano' : 'Novo plano'} maxWidth="max-w-4xl">
        <form onSubmit={submitPlan} className="mt-5 grid gap-4 sm:grid-cols-2">
          <InputField label="Código" required value={planForm.code} disabled={Boolean(planForm.id)} error={planErrors.code} onChange={(event) => setPlanForm((current) => ({ ...current, code: event.target.value }))} />
          <InputField label="Nome" required value={planForm.name} error={planErrors.name} onChange={(event) => setPlanForm((current) => ({ ...current, name: event.target.value }))} />
          <SelectField label="Ciclo" required value={planForm.billingCycle} options={BILLING_CYCLE_OPTIONS} onChange={(event) => setPlanForm((current) => ({ ...current, billingCycle: event.target.value }))} />
          <InputField label="Preço" required value={planForm.priceAmount} error={planErrors.priceAmount} onChange={(event) => setPlanForm((current) => ({ ...current, priceAmount: event.target.value }))} />
          <InputField label="Limite de mesas" required value={planForm.tableLimit} error={planErrors.tableLimit} onChange={(event) => setPlanForm((current) => ({ ...current, tableLimit: event.target.value }))} />
          <InputField label="Limite de restaurantes" required value={planForm.restaurantLimit} error={planErrors.restaurantLimit} onChange={(event) => setPlanForm((current) => ({ ...current, restaurantLimit: event.target.value }))} />
          <InputField label="Label da landing" value={planForm.landingLabel} onChange={(event) => setPlanForm((current) => ({ ...current, landingLabel: event.target.value }))} />
          <InputField label="Display order" value={planForm.displayOrder} onChange={(event) => setPlanForm((current) => ({ ...current, displayOrder: event.target.value }))} />
          <InputField label="Resumo da landing" className="sm:col-span-2" value={planForm.landingCaption} onChange={(event) => setPlanForm((current) => ({ ...current, landingCaption: event.target.value }))} />
          <TextAreaField label="Nota da landing" className="sm:col-span-2" value={planForm.landingNote} onChange={(event) => setPlanForm((current) => ({ ...current, landingNote: event.target.value }))} />
          <TextAreaField label="Descrição interna" className="sm:col-span-2" value={planForm.description} onChange={(event) => setPlanForm((current) => ({ ...current, description: event.target.value }))} />
          <div className="sm:col-span-2 grid gap-3 rounded-2xl border border-slate-200 p-4 md:grid-cols-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Plano ativo</span>
              <Switch checked={planForm.isActive} onChange={(checked) => setPlanForm((current) => ({ ...current, isActive: checked }))} className={`${planForm.isActive ? 'bg-indigo-600' : 'bg-slate-300'} relative inline-flex h-6 w-11 items-center rounded-full`}><span className={`${planForm.isActive ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`} /></Switch>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Destaque na landing</span>
              <Switch checked={planForm.isFeatured} onChange={(checked) => setPlanForm((current) => ({ ...current, isFeatured: checked }))} className={`${planForm.isFeatured ? 'bg-indigo-600' : 'bg-slate-300'} relative inline-flex h-6 w-11 items-center rounded-full`}><span className={`${planForm.isFeatured ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`} /></Switch>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Visível no site</span>
              <Switch checked={planForm.isPubliclyVisible} onChange={(checked) => setPlanForm((current) => ({ ...current, isPubliclyVisible: checked }))} className={`${planForm.isPubliclyVisible ? 'bg-indigo-600' : 'bg-slate-300'} relative inline-flex h-6 w-11 items-center rounded-full`}><span className={`${planForm.isPubliclyVisible ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`} /></Switch>
            </div>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-3 border-t border-slate-200 pt-4">
            <button type="button" onClick={() => setPlanModalOpen(false)} className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">Cancelar</button>
            <button type="submit" disabled={planSaving} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60">{planSaving ? 'Salvando...' : 'Salvar plano'}</button>
          </div>
        </form>
      </ModalShell>

      <ModalShell open={subscriptionModalOpen} onClose={() => setSubscriptionModalOpen(false)} title="Nova assinatura" maxWidth="max-w-5xl">
        <form onSubmit={submitSubscription} className="mt-5 grid gap-4 sm:grid-cols-2">
          <SelectField label="Empresa" required value={subscriptionForm.companyId} options={companyOptions} error={subscriptionErrors.companyId} onChange={(event) => setSubscriptionForm((current) => ({ ...current, companyId: event.target.value }))} />
          <SelectField label="Plano" required value={subscriptionForm.planId} options={planOptions} error={subscriptionErrors.planId} onChange={(event) => setSubscriptionForm((current) => ({ ...current, planId: event.target.value }))} />
          <SelectField
            label="Provedor"
            required
            value={subscriptionForm.provider}
            options={PROVIDER_OPTIONS}
            onChange={(event) => setSubscriptionForm((current) => ({
              ...current,
              provider: event.target.value,
              status: event.target.value === 'asaas' ? 'pending_payment' : current.status || 'active',
              providerStatus: event.target.value === 'asaas' ? '' : current.providerStatus,
              activatedAt: event.target.value === 'asaas' ? '' : current.activatedAt,
              currentPeriodStart: event.target.value === 'asaas' ? '' : current.currentPeriodStart,
              currentPeriodEnd: event.target.value === 'asaas' ? '' : current.currentPeriodEnd
            }))}
          />
          <SelectField label="Origem" required value={subscriptionForm.origin} options={MANUAL_SUBSCRIPTION_ORIGIN_OPTIONS} onChange={(event) => setSubscriptionForm((current) => ({ ...current, origin: event.target.value }))} />
          <SelectField
            label="Status"
            required
            value={subscriptionUsesAsaas ? 'pending_payment' : subscriptionForm.status}
            options={subscriptionStatusOptions}
            error={subscriptionErrors.status}
            disabled={subscriptionUsesAsaas}
            onChange={(event) => setSubscriptionForm((current) => ({ ...current, status: event.target.value }))}
          />
          <InputField label="Valor customizado" value={subscriptionForm.priceAmount} error={subscriptionErrors.priceAmount} onChange={(event) => setSubscriptionForm((current) => ({ ...current, priceAmount: event.target.value }))} placeholder="Opcional" />
          {subscriptionForm.provider === 'asaas' ? (
            <>
              <div className="sm:col-span-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Assinaturas integradas ao Asaas são criadas inicialmente como pendentes de pagamento. Ativação, status do provedor e período atual passam a ser controlados pelos webhooks da cobrança.
              </div>
              <SelectField label="Forma no Asaas" required value={subscriptionForm.providerBillingType} options={ADMIN_BILLING_TYPE_OPTIONS} error={subscriptionErrors.providerBillingType} onChange={(event) => setSubscriptionForm((current) => ({ ...current, providerBillingType: event.target.value }))} />
              <InputField label="Próximo vencimento" required type="date" value={subscriptionForm.nextDueAt} error={subscriptionErrors.nextDueAt} onChange={(event) => setSubscriptionForm((current) => ({ ...current, nextDueAt: event.target.value }))} />
              <InputField label="Descrição no Asaas" className="sm:col-span-2" value={subscriptionForm.providerDescription} onChange={(event) => setSubscriptionForm((current) => ({ ...current, providerDescription: event.target.value }))} />
            </>
          ) : null}
          <InputField label="Início da vigência" type="datetime-local" value={subscriptionForm.startedAt} onChange={(event) => setSubscriptionForm((current) => ({ ...current, startedAt: event.target.value }))} />
          {!subscriptionUsesAsaas ? <InputField label="Data de ativação" type="datetime-local" value={subscriptionForm.activatedAt} onChange={(event) => setSubscriptionForm((current) => ({ ...current, activatedAt: event.target.value }))} /> : null}
          {!subscriptionUsesAsaas ? <InputField label="Período atual: início" type="datetime-local" value={subscriptionForm.currentPeriodStart} onChange={(event) => setSubscriptionForm((current) => ({ ...current, currentPeriodStart: event.target.value }))} /> : null}
          {!subscriptionUsesAsaas ? <InputField label="Período atual: fim" type="datetime-local" value={subscriptionForm.currentPeriodEnd} onChange={(event) => setSubscriptionForm((current) => ({ ...current, currentPeriodEnd: event.target.value }))} /> : null}
          {!subscriptionUsesAsaas ? <InputField label="Status do provedor" value={subscriptionForm.providerStatus} onChange={(event) => setSubscriptionForm((current) => ({ ...current, providerStatus: event.target.value }))} /> : null}
          <InputField label="Referência manual" value={subscriptionForm.manualBillingReference} onChange={(event) => setSubscriptionForm((current) => ({ ...current, manualBillingReference: event.target.value }))} />
          <TextAreaField label="Observações" className="sm:col-span-2" value={subscriptionForm.notes} onChange={(event) => setSubscriptionForm((current) => ({ ...current, notes: event.target.value }))} />
          <div className="sm:col-span-2 flex justify-end gap-3 border-t border-slate-200 pt-4">
            <button type="button" onClick={() => setSubscriptionModalOpen(false)} className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">Cancelar</button>
            <button type="submit" disabled={subscriptionSaving || loadingCompanies} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60">{subscriptionSaving ? 'Salvando...' : 'Criar assinatura'}</button>
          </div>
        </form>
      </ModalShell>

      <ModalShell open={detailOpen} onClose={() => setDetailOpen(false)} title="Detalhes da assinatura" maxWidth="max-w-6xl">
        {detailLoading ? (
          <div className="py-10 text-center text-sm text-slate-500">Carregando detalhes...</div>
        ) : !subscriptionDetail ? (
          <div className="py-10 text-center text-sm text-slate-500">Não foi possível carregar os detalhes desta assinatura.</div>
        ) : (
          <div className="mt-5 space-y-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-xl font-semibold text-slate-900">{subscriptionDetail.company?.name}</h2>
                  <BillingStatusBadge status={subscriptionDetail.status} />
                </div>
                <p className="mt-2 text-sm text-slate-600">Plano {subscriptionDetail.plan?.landingLabel || subscriptionDetail.plan?.name} · {formatMoney(subscriptionDetail.priceAmount, subscriptionDetail.currency)} · {originLabel(subscriptionDetail.origin)}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => openStatusModal(subscriptionDetail)} className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">Alterar status</button>
                <button type="button" onClick={() => openPriceModal(subscriptionDetail)} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">Ajustar valor</button>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-4">
              <div className={metaItemClass}><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Provedor</p><p className="mt-2 text-sm font-semibold text-slate-900">{providerLabel(subscriptionDetail.provider)}</p><p className="mt-1 text-xs text-slate-500">{adminBillingTypeLabel(subscriptionDetail.providerBillingType)}</p></div>
              <div className={metaItemClass}><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Próximo vencimento</p><p className="mt-2 text-sm font-semibold text-slate-900">{formatDateTime(subscriptionDetail.nextDueAt)}</p></div>
              <div className={metaItemClass}><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Período atual</p><p className="mt-2 text-sm font-semibold text-slate-900">{formatDate(subscriptionDetail.currentPeriodStart)} até {formatDate(subscriptionDetail.currentPeriodEnd)}</p></div>
              <div className={metaItemClass}><p className="text-xs font-medium uppercase tracking-wide text-slate-500">Limites do plano</p><p className="mt-2 text-sm font-semibold text-slate-900">{subscriptionDetail.plan?.tableLimit} mesas · {subscriptionDetail.plan?.restaurantLimit} restaurante(s)</p></div>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-900">Dados da cobrança</h3>
                <dl className="mt-4 grid gap-3 text-sm">
                  <div><dt className="font-medium text-slate-500">Status no provedor</dt><dd className="mt-1 text-slate-700">{subscriptionDetail.providerStatus || '-'}</dd></div>
                  <div><dt className="font-medium text-slate-500">Assinatura no Asaas</dt><dd className="mt-1 font-mono text-xs text-slate-700">{subscriptionDetail.providerSubscriptionId || '-'}</dd></div>
                  <div><dt className="font-medium text-slate-500">Checkout</dt><dd className="mt-1 font-mono text-xs text-slate-700">{subscriptionDetail.providerCheckoutId || '-'}</dd></div>
                  <div><dt className="font-medium text-slate-500">Cliente de billing</dt><dd className="mt-1 text-slate-700">{subscriptionDetail.billingCustomer?.name || '-'}</dd></div>
                  <div><dt className="font-medium text-slate-500">Referência manual</dt><dd className="mt-1 text-slate-700">{subscriptionDetail.manualBillingReference || '-'}</dd></div>
                  <div><dt className="font-medium text-slate-500">Observações</dt><dd className="mt-1 whitespace-pre-wrap text-slate-700">{subscriptionDetail.notes || '-'}</dd></div>
                </dl>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-900">Origem e contexto</h3>
                <dl className="mt-4 grid gap-3 text-sm">
                  <div><dt className="font-medium text-slate-500">Criada em</dt><dd className="mt-1 text-slate-700">{formatDateTime(subscriptionDetail.createdAt)}</dd></div>
                  <div><dt className="font-medium text-slate-500">Último evento</dt><dd className="mt-1 text-slate-700">{formatDateTime(subscriptionDetail.lastEventAt)}</dd></div>
                  <div><dt className="font-medium text-slate-500">Responsável</dt><dd className="mt-1 text-slate-700">{subscriptionDetail.managedByName || '-'}</dd></div>
                  <div><dt className="font-medium text-slate-500">Origem</dt><dd className="mt-1 text-slate-700">{originLabel(subscriptionDetail.origin)}</dd></div>
                  <div><dt className="font-medium text-slate-500">Signup público</dt><dd className="mt-1 text-slate-700">{subscriptionDetail.signupRequest?.publicId || '-'}</dd></div>
                </dl>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 xl:col-span-1">
                <h3 className="text-sm font-semibold text-slate-900">Histórico de status</h3>
                <div className="mt-4 space-y-3">
                  {(subscriptionDetail.statusHistory || []).length === 0 ? <p className="text-sm text-slate-500">Sem histórico registrado.</p> : subscriptionDetail.statusHistory.map((item) => (
                    <article key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <BillingStatusBadge status={item.newStatus} />
                        <span className="text-xs text-slate-500">{formatDateTime(item.createdAt)}</span>
                      </div>
                      <p className="mt-2 text-xs text-slate-600">Origem: {originLabel(item.source)}</p>
                      <p className="mt-1 text-xs text-slate-600">{item.reason || 'Sem observação.'}</p>
                    </article>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 xl:col-span-1">
                <h3 className="text-sm font-semibold text-slate-900">Histórico de preço</h3>
                <div className="mt-4 space-y-3">
                  {(subscriptionDetail.priceHistory || []).length === 0 ? <p className="text-sm text-slate-500">Sem ajustes de preço registrados.</p> : subscriptionDetail.priceHistory.map((item) => (
                    <article key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-sm font-semibold text-slate-900">{formatMoney(item.newPriceAmount, item.currency)}</p>
                      <p className="mt-1 text-xs text-slate-500">{formatDateTime(item.createdAt)}</p>
                      <p className="mt-2 text-xs text-slate-600">Origem: {originLabel(item.source)}</p>
                      <p className="mt-1 text-xs text-slate-600">{item.reason || 'Sem observação.'}</p>
                    </article>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 xl:col-span-1">
                <h3 className="text-sm font-semibold text-slate-900">Eventos recentes</h3>
                <div className="mt-4 space-y-3">
                  {(subscriptionDetail.recentEvents || []).length === 0 ? <p className="text-sm text-slate-500">Nenhum evento recente.</p> : subscriptionDetail.recentEvents.map((item) => (
                    <article key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-900">{item.eventType}</p>
                        <BillingStatusBadge status={item.processingStatus} />
                      </div>
                      <p className="mt-2 font-mono text-xs text-slate-500">{item.providerEventId}</p>
                      <p className="mt-1 text-xs text-slate-600">{formatDateTime(item.createdAt)}</p>
                      <p className="mt-1 text-xs text-rose-600">{item.errorMessage || ''}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalShell>

      <ModalShell open={statusModalOpen} onClose={() => setStatusModalOpen(false)} title="Alterar status da assinatura" maxWidth="max-w-3xl">
        <form onSubmit={submitStatusUpdate} className="mt-5 grid gap-4 sm:grid-cols-2">
          {detailUsesAsaas ? (
            <div className="sm:col-span-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Para assinaturas integradas ao Asaas, pendência de pagamento, atraso, status do provedor e período atual são atualizados automaticamente pelos eventos da cobrança.
            </div>
          ) : null}
          <SelectField label="Novo status" required value={statusForm.status} options={statusUpdateOptions} onChange={(event) => setStatusForm((current) => ({ ...current, status: event.target.value }))} />
          {!detailUsesAsaas ? <InputField label="Status no provedor" value={statusForm.providerStatus} onChange={(event) => setStatusForm((current) => ({ ...current, providerStatus: event.target.value }))} /> : null}
          <InputField label="Próximo vencimento" type="datetime-local" value={statusForm.nextDueAt} onChange={(event) => setStatusForm((current) => ({ ...current, nextDueAt: event.target.value }))} />
          {!detailUsesAsaas ? <InputField label="Fim do período atual" type="datetime-local" value={statusForm.currentPeriodEnd} onChange={(event) => setStatusForm((current) => ({ ...current, currentPeriodEnd: event.target.value }))} /> : null}
          <TextAreaField label="Motivo / observação" className="sm:col-span-2" value={statusForm.reason} onChange={(event) => setStatusForm((current) => ({ ...current, reason: event.target.value }))} />
          <div className="sm:col-span-2 flex justify-end gap-3 border-t border-slate-200 pt-4">
            <button type="button" onClick={() => setStatusModalOpen(false)} className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">Cancelar</button>
            <button type="submit" disabled={statusSaving} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60">{statusSaving ? 'Salvando...' : 'Salvar status'}</button>
          </div>
        </form>
      </ModalShell>

      <ModalShell open={priceModalOpen} onClose={() => setPriceModalOpen(false)} title="Ajustar valor da assinatura" maxWidth="max-w-3xl">
        <form onSubmit={submitPriceUpdate} className="mt-5 grid gap-4 sm:grid-cols-2">
          <InputField label="Novo valor" required value={priceForm.priceAmount} error={priceErrors.priceAmount} onChange={(event) => setPriceForm((current) => ({ ...current, priceAmount: event.target.value }))} />
          <InputField label="Moeda" value={priceForm.currency} onChange={(event) => setPriceForm((current) => ({ ...current, currency: event.target.value.toUpperCase() }))} />
          <TextAreaField label="Motivo / observação" className="sm:col-span-2" value={priceForm.reason} onChange={(event) => setPriceForm((current) => ({ ...current, reason: event.target.value }))} />
          <div className="sm:col-span-2 flex justify-end gap-3 border-t border-slate-200 pt-4">
            <button type="button" onClick={() => setPriceModalOpen(false)} className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">Cancelar</button>
            <button type="submit" disabled={priceSaving} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60">{priceSaving ? 'Salvando...' : 'Salvar valor'}</button>
          </div>
        </form>
      </ModalShell>
    </div>
  );
}
